import { FC, useEffect, useMemo, useState } from "react";
import { Virtuoso } from "react-virtuoso";
import { IconDefinition, IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Combobox,
  ComboboxProps,
  Group,
  Input,
  Loader,
  Pill,
  PillsInput,
  Text,
  Tooltip,
  TooltipProps,
  useCombobox,
} from "@mantine/core";

import {
  FilterTreeSelectItemData,
  TreeSelectItem,
} from "./FilterTreeSelectItem";

const getSelectedOptionsFromValues = (
  values: string[],
  dataDict: { [key: string]: FilterTreeSelectItemData }
) => {
  const selectedOptions: { [option: string]: string[] } = {};

  values.forEach((value) => {
    const option = dataDict[value];
    if (option) {
      const parent = dataDict[option.parent ?? ""];
      if (option.suboptions) {
        selectedOptions[value] = option.suboptions;
      } else if (parent) {
        if (parent.value in selectedOptions) {
          selectedOptions[parent.value].push(value);
        } else {
          selectedOptions[parent.value] = [value];
        }
      } else {
        selectedOptions[value] = [];
      }
    }
  });

  return selectedOptions;
};

export interface FooterMessage {
  title: string;
  message: string;
}

export interface FilterTreeSelectProps {
  data: FilterTreeSelectItemData[];
  value: string[];
  onChange: (value: string[]) => void;
  label?: string;
  searchable?: boolean;
  clearable?: boolean;
  placeholder?: string;
  disabled?: boolean;
  disabledMessage?: string;
  withTooltips?: boolean;
  tooltipProps?: TooltipProps;
  comboboxProps?: ComboboxProps;
  nothingFoundMessage?: string;
  isLoading?: boolean;
  maxPills?: number;
  loading?: boolean;
  tooltip?: React.ReactNode;
  tooltipIcon?: IconProp;
  footerMessage?: FooterMessage;
  withTopLevelRadioSelect?: boolean;
}

// TODO: Combine this component with Multiselect so we don't support two versions  of (mostly) the same thing
export const FilterTreeSelect: FC<FilterTreeSelectProps> = ({
  data,
  label,
  value,
  onChange,
  searchable = true,
  clearable,
  placeholder,
  disabled,
  disabledMessage,
  withTooltips = true,
  comboboxProps = {},
  tooltipProps = {},
  nothingFoundMessage,
  isLoading,
  maxPills,
  loading,
  tooltip,
  tooltipIcon,
  footerMessage,
  withTopLevelRadioSelect = false,
}) => {
  const [search, setSearch] = useState("");
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
    onDropdownOpen: () => combobox.updateSelectedOptionIndex("active"),
  });

  const dataDict: { [key: string]: FilterTreeSelectItemData } = useMemo(() => {
    const res: { [key: string]: FilterTreeSelectItemData } = {};
    for (const dataItem of data) {
      res[dataItem.value] = dataItem;
    }
    return res;
  }, [data]);

  const selectedOptions = useMemo(
    () => getSelectedOptionsFromValues(value, dataDict),
    [dataDict, value]
  );

  const [expandedStates, setExpandedStates] = useState<{
    [option: string]: boolean;
  }>({});

  useEffect(() => {
    const initialState: { [option: string]: boolean } = {};
    data.forEach((item) => {
      if (item.suboptions) {
        initialState[item.value] = true;
      }
    });
    setExpandedStates(initialState);
  }, [data]);

  const { labelReference, iconReference } = useMemo(() => {
    const labelReference: Record<string, string> = {};
    const iconReference: Record<string, IconDefinition> = {};

    data.forEach((item) => {
      labelReference[item.value] = item.label;
      if (item.icon) {
        iconReference[item.value] = item.icon;
      }
    });

    return { labelReference, iconReference };
  }, [data]);

  const handleValueRemove = (val: string) => {
    const newVals = value.filter((v) => v !== val);
    onChange(newVals);
  };

  const valuePills = value.map((value) => (
    <Tooltip
      {...tooltipProps}
      label={labelReference[value]}
      disabled={!withTooltips}
      key={value}
    >
      <Pill
        key={value}
        withRemoveButton
        onRemove={() => handleValueRemove(value)}
        variant="select-pill"
      >
        <Group gap={4} wrap="nowrap">
          {value in iconReference && (
            <FontAwesomeIcon icon={iconReference[value]} />
          )}
          <Text inherit truncate>
            {labelReference[value] || value}
          </Text>
        </Group>
      </Pill>
    </Tooltip>
  ));

  const maxPillsReached = maxPills && value.length >= maxPills;

  const MaxValuePill = () => {
    return (
      <Pill
        variant="select-pill"
        withRemoveButton
        onRemove={() => onChange([])}
      >
        {value.length} selected
      </Pill>
    );
  };

  const filteredOptions = data.filter((item) =>
    item.label.toLowerCase().includes(search.trim().toLowerCase())
  );

  const options = (
    <Virtuoso
      style={{ height: 300 }}
      data={filteredOptions}
      itemContent={(_, item) => (
        <TreeSelectItem
          {...item}
          key={item.value}
          setSelectedValues={onChange}
          dataDict={dataDict}
          selectedOptions={selectedOptions}
          expandedStates={expandedStates}
          setExpandedStates={setExpandedStates}
          withTopLevelRadioSelect={withTopLevelRadioSelect}
        />
      )}
    />
  );

  return (
    <Combobox store={combobox} {...comboboxProps}>
      <Combobox.DropdownTarget>
        <PillsInput
          label={
            <Group mb={7}>
              <Text fz="md" fw={500} c="dark.4">
                {label}
              </Text>
              {tooltipIcon && (
                <Tooltip
                  label={tooltip}
                  withinPortal
                  w={300}
                  multiline
                  position="right-start"
                >
                  <Text fz="md" fw={500} c="gray.6">
                    <FontAwesomeIcon icon={tooltipIcon} />
                  </Text>
                </Tooltip>
              )}
            </Group>
          }
          onClick={() => combobox.openDropdown()}
          pointer={!searchable}
          multiline
          fw={500}
          rightSection={
            loading ? (
              <Loader size="1rem" />
            ) : clearable && value.length > 0 ? (
              <Combobox.ClearButton
                size="sm"
                onMouseDown={(event) => event.preventDefault()}
                onClear={() => onChange([])}
                aria-label="Clear value"
              />
            ) : (
              <Combobox.Chevron />
            )
          }
        >
          <Pill.Group fw="initial">
            {!maxPillsReached && Boolean(value.length > 0) && valuePills}
            {maxPillsReached && <MaxValuePill />}

            {!value.length && !searchable && (
              <Input.Placeholder>
                {placeholder || "Select a value"}
              </Input.Placeholder>
            )}
            <Combobox.EventsTarget>
              <Tooltip
                label={disabledMessage || "This filter is disabled."}
                disabled={!disabled}
                withinPortal
              >
                <PillsInput.Field
                  type={searchable ? "visible" : "hidden"}
                  onFocus={() => combobox.openDropdown()}
                  onBlur={() => combobox.closeDropdown()}
                  value={search}
                  placeholder={
                    value.length > 0
                      ? undefined
                      : placeholder || "Search values"
                  }
                  onChange={
                    searchable
                      ? (event) => {
                          combobox.updateSelectedOptionIndex();
                          setSearch(event.currentTarget.value);
                        }
                      : undefined
                  }
                  onKeyDown={
                    searchable
                      ? (event) => {
                          if (
                            event.key === "Backspace" &&
                            search.length === 0
                          ) {
                            event.preventDefault();
                            handleValueRemove(value[value.length - 1]);
                          }
                        }
                      : undefined
                  }
                  disabled={disabled}
                />
              </Tooltip>
            </Combobox.EventsTarget>
          </Pill.Group>
        </PillsInput>
      </Combobox.DropdownTarget>

      {combobox.dropdownOpened && (
        <Combobox.Dropdown miw={300}>
          <Combobox.Options mah={300} style={{ overflowY: "auto" }}>
            {isLoading && <Combobox.Empty>Loading...</Combobox.Empty>}
            {!isLoading && filteredOptions.length > 0 && options}
            {!isLoading && filteredOptions.length <= 0 && (
              <Combobox.Empty>
                {nothingFoundMessage || "Nothing found..."}
              </Combobox.Empty>
            )}
          </Combobox.Options>
          {footerMessage && (
            <Combobox.Footer my={0}>
              <Text fw={500}>{footerMessage?.title}</Text>
              <div>{footerMessage?.message}</div>
            </Combobox.Footer>
          )}
        </Combobox.Dropdown>
      )}
    </Combobox>
  );
};
